import type { BadgeTone } from '@/components/ui/badge'
import type { AdAccountDto } from '@lib/dto/ads.dto'

export interface AdAccountStatus {
  label: string
  tone: BadgeTone
}

/**
 * What an account's status reads as, in the spec's words
 * (docs/ad-analytics.spec.md, "Ad accounts" and "When a connection stops
 * working"). A closed account says so whatever the company does with it: nothing
 * else about it matters until Google reopens it.
 */
export function adAccountStatus(account: AdAccountDto, failingConnectionIds: ReadonlySet<string> = new Set()): AdAccountStatus {
  if (account.closedSince !== null) return { label: 'Closed in Google', tone: 'neutral' }
  switch (account.use) {
    case 'not_in_use':
      return { label: 'Not in use', tone: 'info' }
    case 'ignored':
      return { label: 'Ignored', tone: 'neutral' }
    case 'stopped':
      return { label: 'Stopped', tone: 'neutral' }
    case 'in_use':
      if (account.defaultVenueDeleted) return { label: 'Venue deleted', tone: 'warning' }
      if (account.unreachableSince !== null) return { label: 'Can’t be reached', tone: 'danger' }
      // Its connections were removed on purpose: nothing is broken, it just needs one.
      if (account.connectionIds.length === 0) return { label: 'No connection reaches it', tone: 'warning' }
      if (account.connectionIds.every((id) => failingConnectionIds.has(id))) return { label: 'Sign-in not working', tone: 'danger' }
      if (!account.historyComplete) return { label: 'Importing history', tone: 'info' }
      if (account.importing) return { label: 'Updating', tone: 'info' }
      return { label: 'In use', tone: 'success' }
  }
}
